import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Factory, Layers, Plus, Truck } from 'lucide-react';
import {
  adminApi,
  catalogApi,
  type AdminVendor,
  type AssetTypeFields,
} from '@/api/endpoints';
import { queryKeys } from '@/api/queryKeys';
import { ApiError } from '@/api/client';
import type { AssetModel, AssetType, Manufacturer, CustomFieldDefinition, CustomFieldType } from '@/api/types';
import { usePermission } from '@/auth/AuthProvider';
import { PERMISSIONS } from '@/auth/permissions';
import { Button } from '@/design-system/Button';
import { DataTable, type Column } from '@/design-system/DataTable';
import { InlineError } from '@/design-system/Feedback';
import { Checkbox, Select, TextArea, TextInput } from '@/design-system/Inputs';
import { Tabs } from '@/design-system/Layout';
import { Dialog } from '@/design-system/Overlay';
import { humanise } from '@/lib/format';
import { ActiveBadge, asPage, SettingsCard, useSettingsSave } from './shared';

/**
 * The asset catalogue: the types, models, manufacturers and vendors people choose from when they
 * register or buy an asset.
 *
 * Shared by every organisation, so changing it needs its own permission. As with the organisation
 * structure, nothing is deleted - a discontinued model is still the model of every device bought
 * before - so retiring an entry is deactivating it.
 */

const CATEGORIES = ['LAPTOP', 'DESKTOP', 'MONITOR', 'MOBILE', 'TABLET', 'NETWORK', 'PERIPHERAL', 'SERVER', 'OTHER'];

type Tab = 'types' | 'models' | 'manufacturers' | 'vendors';

export function CatalogSection() {
  const canWrite = usePermission(PERMISSIONS.CATALOG_WRITE);
  const [tab, setTab] = useState<Tab>('types');
  const [editingType, setEditingType] = useState<AssetType | 'new' | null>(null);
  const [editingModel, setEditingModel] = useState<AssetModel | 'new' | null>(null);
  const [editingManufacturer, setEditingManufacturer] = useState<Manufacturer | 'new' | null>(null);
  const [editingVendor, setEditingVendor] = useState<AdminVendor | 'new' | null>(null);

  const types = useQuery({ queryKey: queryKeys.admin.assetTypes(), queryFn: adminApi.allAssetTypes });
  const models = useQuery({
    queryKey: queryKeys.admin.assetModels(),
    queryFn: () => catalogApi.assetModels(),
  });
  const manufacturers = useQuery({
    queryKey: queryKeys.admin.manufacturers(),
    queryFn: catalogApi.manufacturers,
  });
  const vendors = useQuery({
    queryKey: queryKeys.admin.vendors(),
    queryFn: adminApi.vendors,
  });

  const typeName = useMemo(
    () => new Map((types.data ?? []).map((type) => [type.id ?? '', type.name ?? ''])),
    [types.data],
  );
  const manufacturerName = useMemo(
    () => new Map((manufacturers.data ?? []).map((m) => [m.id ?? '', m.name ?? ''])),
    [manufacturers.data],
  );

  const addButton = (label: string, onClick: () => void) =>
    canWrite && (
      <Button variant="primary" size="sm" leadingIcon={<Plus className="size-4" />} onClick={onClick}>
        {label}
      </Button>
    );

  const typeColumns: Column<AssetType>[] = [
    {
      id: 'name',
      header: 'Type',
      cell: (row) => (
        <span>
          <span className="block text-small font-medium text-text-primary">{row.name}</span>
          <span className="font-mono text-caption text-text-muted">{row.code}</span>
        </span>
      ),
    },
    { id: 'category', header: 'Category', cell: (row) => humanise(row.category) },
    {
      id: 'rules',
      header: 'Before issue, needs',
      cell: (row) =>
        [
          row.requiresAssetTag && 'asset tag',
          row.requiresEndpointAgent && 'endpoint agent',
          row.requiresMdmEnrolment && 'MDM enrolment',
        ]
          .filter(Boolean)
          .join(', ') || <span className="text-text-muted">nothing</span>,
    },
    { id: 'status', header: 'Status', cell: (row) => <ActiveBadge active={Boolean(row.active)} /> },
  ];

  const modelColumns: Column<AssetModel>[] = [
    {
      id: 'name',
      header: 'Model',
      cell: (row) => (
        <span>
          <span className="block text-small font-medium text-text-primary">{row.modelName}</span>
          <span className="text-caption text-text-muted">{row.modelNumber ?? ''}</span>
        </span>
      ),
    },
    { id: 'manufacturer', header: 'Manufacturer', cell: (row) => manufacturerName.get(row.manufacturerId ?? '') ?? '—' },
    { id: 'type', header: 'Type', cell: (row) => typeName.get(row.assetTypeId ?? '') ?? '—' },
    { id: 'status', header: 'Status', cell: (row) => <ActiveBadge active={Boolean(row.active)} /> },
  ];

  const manufacturerColumns: Column<Manufacturer>[] = [
    { id: 'name', header: 'Manufacturer', cell: (row) => <span className="font-medium text-text-primary">{row.name}</span> },
    { id: 'status', header: 'Status', cell: (row) => <ActiveBadge active={Boolean(row.active)} /> },
  ];

  const vendorColumns: Column<AdminVendor>[] = [
    {
      id: 'name',
      header: 'Vendor',
      cell: (row) => (
        <span>
          <span className="block text-small font-medium text-text-primary">{row.name}</span>
          <span className="font-mono text-caption text-text-muted">{row.code}</span>
        </span>
      ),
    },
    { id: 'contact', header: 'Contact', cell: (row) => row.contactEmail ?? <span className="text-text-muted">—</span> },
    { id: 'status', header: 'Status', cell: (row) => <ActiveBadge active={row.active} /> },
  ];

  return (
    <div className="space-y-5">
      <Tabs
        active={tab}
        onChange={(id) => setTab(id as Tab)}
        tabs={[
          { id: 'types', label: 'Asset types', count: types.data?.length },
          { id: 'models', label: 'Models', count: models.data?.length },
          { id: 'manufacturers', label: 'Manufacturers', count: manufacturers.data?.length },
          { id: 'vendors', label: 'Vendors', count: vendors.data?.length },
        ]}
      />

      {tab === 'types' && (
        <SettingsCard
          title="Asset types"
          description="What kinds of device the register holds, and what each needs before it can be issued."
          actions={addButton('Add type', () => setEditingType('new'))}
        >
          <DataTable
            caption="Asset types"
            exportable={false}
            columns={typeColumns}
            page={asPage(types.data)}
            isLoading={types.isLoading}
            error={types.error}
            getRowId={(row) => row.id ?? ''}
            onRowClick={canWrite ? setEditingType : undefined}
            empty={{ icon: <Layers className="size-5" />, title: 'No asset types yet' }}
          />
        </SettingsCard>
      )}

      {tab === 'models' && (
        <SettingsCard
          title="Models"
          description="Specific products. A model's manufacturer and type are fixed once it exists."
          actions={addButton('Add model', () => setEditingModel('new'))}
        >
          <DataTable
            caption="Models"
            exportable={false}
            columns={modelColumns}
            page={asPage(models.data)}
            isLoading={models.isLoading}
            error={models.error}
            getRowId={(row) => row.id ?? ''}
            onRowClick={canWrite ? setEditingModel : undefined}
            empty={{ icon: <Boxes className="size-5" />, title: 'No models yet' }}
          />
        </SettingsCard>
      )}

      {tab === 'manufacturers' && (
        <SettingsCard
          title="Manufacturers"
          actions={addButton('Add manufacturer', () => setEditingManufacturer('new'))}
        >
          <DataTable
            caption="Manufacturers"
            exportable={false}
            columns={manufacturerColumns}
            page={asPage(manufacturers.data)}
            isLoading={manufacturers.isLoading}
            error={manufacturers.error}
            getRowId={(row) => row.id ?? ''}
            onRowClick={canWrite ? setEditingManufacturer : undefined}
            empty={{ icon: <Factory className="size-5" />, title: 'No manufacturers yet' }}
          />
        </SettingsCard>
      )}

      {tab === 'vendors' && (
        <SettingsCard
          title="Vendors"
          description="Who assets are bought from."
          actions={addButton('Add vendor', () => setEditingVendor('new'))}
        >
          <DataTable
            caption="Vendors"
            exportable={false}
            columns={vendorColumns}
            page={asPage(vendors.data)}
            isLoading={vendors.isLoading}
            error={vendors.error}
            getRowId={(row) => row.id}
            onRowClick={canWrite ? setEditingVendor : undefined}
            empty={{ icon: <Truck className="size-5" />, title: 'No vendors yet' }}
          />
        </SettingsCard>
      )}

      {!canWrite && (
        <p className="text-caption text-text-muted">
          You can view the catalogue. Changing it needs the catalogue write permission.
        </p>
      )}

      <TypeDialog value={editingType} currentType={editingType && editingType !== 'new' ? types.data?.find(type => type.id === editingType.id) : undefined} typeFetching={types.isFetching} typeLoadFailed={Boolean(types.error)} canWrite={canWrite} onClose={() => setEditingType(null)} />
      <ModelDialog
        value={editingModel}
        onClose={() => setEditingModel(null)}
        types={types.data ?? []}
        manufacturers={manufacturers.data ?? []}
      />
      <ManufacturerDialog value={editingManufacturer} onClose={() => setEditingManufacturer(null)} />
      <VendorDialog value={editingVendor} onClose={() => setEditingVendor(null)} />
    </div>
  );
}

// Every catalogue change also refreshes the pickers the rest of the product reads.
const CATALOG_KEYS = [queryKeys.admin.all, ['catalog']] as const;

function DialogFrame({
  open,
  title,
  onClose,
  onSave,
  saving,
  saveDisabled = false,
  saveLabel,
  children,
  otherError,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  onSave: () => void;
  saving: boolean;
  saveDisabled?: boolean;
  saveLabel: string;
  children: React.ReactNode;
  otherError: unknown;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      busy={saving}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} disabled={saving || saveDisabled} onClick={onSave}>
            {saveLabel}
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(event) => event.preventDefault()} noValidate>
        {typeof otherError === 'string' ? <p role="alert">{otherError}</p> : otherError ? <InlineError error={otherError} /> : null}
        {children}
      </form>
    </Dialog>
  );
}

interface TypeForm extends Omit<AssetTypeFields, 'usefulLifeMonths' | 'salvagePercent'> {
  usefulLifeMonths: string;
  salvagePercent: string;
  code: string;
  active: boolean;
}

function TypeDialog({ value, currentType, typeFetching, typeLoadFailed, canWrite, onClose }: { value: AssetType | 'new' | null; currentType?: AssetType; typeFetching: boolean; typeLoadFailed: boolean; canWrite: boolean; onClose: () => void }) {
  const isNew = value === 'new';
  const existing = value && value !== 'new' ? value : null;
  const [otherError, setOtherError] = useState<unknown>(null);
  const [locked, setLocked] = useState(false);
  const [openingVersion, setOpeningVersion] = useState<number>();
  const [openingSchema, setOpeningSchema] = useState<CustomFieldDefinition[]>([]);
  const [blockedSnapshot, setBlockedSnapshot] = useState('');
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset, setError, formState } = useForm<TypeForm>();
  const [customFieldSchema, setCustomFieldSchema] = useState<CustomFieldDefinition[]>([]);

  useEffect(() => {
    setOtherError(null);
    setLocked(false);
    reset({
      code: existing?.code ?? '',
      name: existing?.name ?? '',
      category: existing?.category ?? 'LAPTOP',
      requiresAssetTag: existing?.requiresAssetTag ?? true,
      requiresEndpointAgent: existing?.requiresEndpointAgent ?? false,
      requiresMdmEnrolment: existing?.requiresMdmEnrolment ?? false,
      requiresSanitisation: existing && 'requiresSanitisation' in existing ? existing.requiresSanitisation !== false : true,
      usefulLifeMonths: existing?.usefulLifeMonths?.toString() ?? '',
      salvagePercent: existing?.salvagePercent?.toString() ?? '',
      active: existing?.active ?? true,
    });
    setCustomFieldSchema(existing?.customFieldSchema ?? []);
    setOpeningVersion(existing?.version);
    const schema = existing?.customFieldSchema ?? [];
    setOpeningSchema(schema.map(field => ({ ...field })));
    setLocked(Boolean(existing && blockedSnapshot === `${existing.version}:${JSON.stringify(schema)}`));
  }, [value === null ? null : value === 'new' ? 'new' : value.id, reset]);

  const currentSnapshotChanged = Boolean(existing && currentType && (currentType.version !== openingVersion || JSON.stringify(currentType.customFieldSchema ?? []) !== JSON.stringify(openingSchema)));
  const refreshRequired = typeFetching || typeLoadFailed || !canWrite || currentSnapshotChanged || Boolean(existing && !currentType);

  const save = useSettingsSave<TypeForm, AssetType>({
    save: ({ code, active, usefulLifeMonths, salvagePercent, ...base }) => {
      const fields = { ...base, usefulLifeMonths: usefulLifeMonths === '' ? undefined : Number(usefulLifeMonths), salvagePercent: salvagePercent === '' ? undefined : Number(salvagePercent) };
      return existing ? adminApi.updateAssetType(existing.id!, { fields: { ...fields, customFieldSchema }, active, expectedVersion: openingVersion }) : adminApi.createAssetType({ code, fields: { ...fields, customFieldSchema } });
    },
    invalidate: [...CATALOG_KEYS, queryKeys.assets.all, queryKeys.reports.all],
    successMessage: isNew ? 'Asset type added' : 'Asset type saved',
    setError: (field, error) => {
      if (field === 'customFieldSchema') { setOtherError(error.message ?? 'The custom field definition was rejected.'); return; }
      setError(field, error);
    },
    onDone: onClose,
    onOtherError: (error) => {
      setOtherError(error);
      if (error instanceof ApiError && (error.status === 0 || error.status >= 500 || error.status === 409 || error.status === 401 || error.status === 403)) {
        setLocked(true);
        if (existing) setBlockedSnapshot(`${openingVersion}:${JSON.stringify(openingSchema)}`);
        CATALOG_KEYS.forEach(key => void queryClient.invalidateQueries({ queryKey: key }));
      }
    },
  });

  const saveType = handleSubmit((form) => {
    if (locked || refreshRequired) return;
    if (existing && openingVersion == null) { setOtherError('This asset type has no captured version. Refresh the catalogue before saving.'); return; }
    save.mutate(form);
  });

  return (
    <DialogFrame
      open={value !== null}
      title={isNew ? 'Add asset type' : `Edit ${existing?.name}`}
      onClose={onClose}
      onSave={saveType}
      saving={save.isPending}
      saveDisabled={locked || refreshRequired}
      saveLabel={isNew ? 'Add type' : 'Save'}
      otherError={otherError}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <TextInput
          label="Code"
          required={isNew}
          disabled={!isNew}
          placeholder="LAPTOP-STD"
          error={formState.errors.code?.message}
          {...register('code', {
            required: isNew ? 'A code is required' : false,
            pattern: { value: /^[A-Za-z0-9_-]+$/, message: 'Letters, digits, _ and - only' },
          })}
        />
        <TextInput
          label="Name"
          required
          error={formState.errors.name?.message}
          {...register('name', { required: 'A name is required' })}
        />
      </div>
      <Select
        label="Category"
        options={CATEGORIES.map((category) => ({ value: category, label: humanise(category) }))}
        {...register('category')}
      />
      <div className="space-y-2 rounded-lg bg-surface-sunken p-3">
        <p className="text-label text-text-muted">Before an asset of this type can be issued</p>
        <Checkbox label="It must carry an asset tag" {...register('requiresAssetTag')} />
        <Checkbox label="An endpoint agent must be reporting" {...register('requiresEndpointAgent')} />
        <Checkbox label="It must be enrolled in mobile device management" {...register('requiresMdmEnrolment')} />
      </div>
      <Checkbox label="Sanitisation evidence is required before disposal"
        description="Keep this enabled for types that can store data. Disable only for assets without data storage."
        {...register('requiresSanitisation')} />
      <div className="space-y-3 rounded-lg bg-surface-sunken p-3">
        <p className="text-label">Straight-line valuation policy</p>
        <p className="text-small text-text-muted">Shared across all organizations using this asset type. No life or salvage policy is assumed. Blank fields on an existing type keep its current settings.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextInput label="Useful life (months)" type="number" min="1" max="1200" step="1" error={formState.errors.usefulLifeMonths?.message}
            {...register('usefulLifeMonths', { validate: value => value === '' || (/^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= 1200) || 'Use a whole number from 1 to 1200' })} />
          <TextInput label="Salvage percentage" type="number" min="0" max="100" step="0.01" error={formState.errors.salvagePercent?.message}
            {...register('salvagePercent', { validate: value => value === '' || (/^\d+(?:\.\d{1,2})?$/.test(value) && Number(value) <= 100) || 'Use 0 to 100 with at most two decimal places' })} />
        </div>
      </div>
      {!isNew && <Checkbox label="Active" description="Inactive types cannot be chosen for new assets." {...register('active')} />}
      <section className="space-y-3 rounded-lg border border-border-subtle p-4">
        {currentSnapshotChanged && <p role="alert">This asset type changed while you were editing. Close the dialog, refresh, and review the current definition before saving.</p>}
        {(typeFetching || typeLoadFailed) && <p role="alert">The catalogue must finish refreshing successfully before you can save.</p>}
        {!canWrite && <p role="alert">Your catalogue write permission was removed. Close this dialog.</p>}
        {locked && <p role="alert">The save was refused or its response was uncertain. Refresh the catalogue and reopen the type to review before saving again.</p>}
        <div className="flex items-center justify-between"><div><p className="text-label">Optional custom fields</p><p className="text-small text-text-muted">Up to 32 fields. Removing or changing the type of a used field is refused.</p></div><Button size="sm" disabled={save.isPending || customFieldSchema.length >= 32} onClick={() => setCustomFieldSchema([...customFieldSchema, { key: '', label: '', type: 'TEXT' }])}>Add field</Button></div>
        {customFieldSchema.map((field, index) => <div key={index} className="grid gap-2 sm:grid-cols-[1fr_1fr_9rem_auto]"><TextInput label="Key" disabled={save.isPending} value={field.key} maxLength={40} placeholder="asset_code" onChange={e => setCustomFieldSchema(customFieldSchema.map((item, i) => i === index ? { ...item, key: e.target.value } : item))} /><TextInput label="Label" disabled={save.isPending} value={field.label} maxLength={80} onChange={e => setCustomFieldSchema(customFieldSchema.map((item, i) => i === index ? { ...item, label: e.target.value } : item))} /><Select label="Type" disabled={save.isPending} value={field.type} options={(['TEXT', 'NUMBER', 'BOOLEAN', 'DATE'] as CustomFieldType[]).map(type => ({ value: type, label: humanise(type) }))} onChange={e => setCustomFieldSchema(customFieldSchema.map((item, i) => i === index ? { ...item, type: e.target.value as CustomFieldType } : item))} /><Button disabled={save.isPending} variant="ghost" aria-label={`Remove ${field.label || 'field'}`} onClick={() => setCustomFieldSchema(customFieldSchema.filter((_, i) => i !== index))}>Remove</Button></div>)}
      </section>
    </DialogFrame>
  );
}

interface ModelForm {
  manufacturerId: string;
  assetTypeId: string;
  modelName: string;
  modelNumber: string;
  specification: string;
  active: boolean;
}

function ModelDialog({
  value,
  onClose,
  types,
  manufacturers,
}: {
  value: AssetModel | 'new' | null;
  onClose: () => void;
  types: AssetType[];
  manufacturers: Manufacturer[];
}) {
  const isNew = value === 'new';
  const existing = value && value !== 'new' ? value : null;
  const [otherError, setOtherError] = useState<unknown>(null);
  const { register, handleSubmit, reset, setError, formState } = useForm<ModelForm>();

  useEffect(() => {
    setOtherError(null);
    reset({
      manufacturerId: existing?.manufacturerId ?? '',
      assetTypeId: existing?.assetTypeId ?? '',
      modelName: existing?.modelName ?? '',
      modelNumber: existing?.modelNumber ?? '',
      specification: existing?.specification ?? '',
      active: existing?.active ?? true,
    });
  }, [value, existing, reset]);

  const save = useSettingsSave<ModelForm, AssetModel>({
    save: (form) =>
      existing
        ? adminApi.updateAssetModel(existing.id!, {
            modelName: form.modelName,
            modelNumber: form.modelNumber || undefined,
            specification: form.specification || undefined,
            active: form.active,
          })
        : adminApi.createAssetModel({
            manufacturerId: form.manufacturerId,
            assetTypeId: form.assetTypeId,
            modelName: form.modelName,
            modelNumber: form.modelNumber || undefined,
            specification: form.specification || undefined,
          }),
    invalidate: CATALOG_KEYS,
    successMessage: isNew ? 'Model added' : 'Model saved',
    setError,
    onDone: onClose,
    onOtherError: setOtherError,
  });

  return (
    <DialogFrame
      open={value !== null}
      title={isNew ? 'Add model' : `Edit ${existing?.modelName}`}
      onClose={onClose}
      onSave={handleSubmit((form) => save.mutate(form))}
      saving={save.isPending}
      saveLabel={isNew ? 'Add model' : 'Save'}
      otherError={otherError}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          label="Manufacturer"
          required
          disabled={!isNew}
          placeholder="Choose"
          error={formState.errors.manufacturerId?.message}
          options={manufacturers
            .filter((m) => m.active || m.id === existing?.manufacturerId)
            .map((m) => ({ value: m.id ?? '', label: m.name ?? '' }))}
          {...register('manufacturerId', { required: isNew ? 'Choose a manufacturer' : false })}
        />
        <Select
          label="Asset type"
          required
          disabled={!isNew}
          placeholder="Choose"
          error={formState.errors.assetTypeId?.message}
          options={types
            .filter((t) => t.active || t.id === existing?.assetTypeId)
            .map((t) => ({ value: t.id ?? '', label: t.name ?? '' }))}
          {...register('assetTypeId', { required: isNew ? 'Choose a type' : false })}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextInput
          label="Model name"
          required
          error={formState.errors.modelName?.message}
          {...register('modelName', { required: 'A model name is required' })}
        />
        <TextInput label="Model number" {...register('modelNumber')} />
      </div>
      <TextArea label="Specification" rows={3} placeholder="CPU, memory, storage…" {...register('specification')} />
      {!isNew && <Checkbox label="Active" description="Inactive models cannot be chosen for new assets." {...register('active')} />}
    </DialogFrame>
  );
}

interface ManufacturerForm {
  name: string;
  active: boolean;
}

function ManufacturerDialog({ value, onClose }: { value: Manufacturer | 'new' | null; onClose: () => void }) {
  const isNew = value === 'new';
  const existing = value && value !== 'new' ? value : null;
  const [otherError, setOtherError] = useState<unknown>(null);
  const { register, handleSubmit, reset, setError, formState } = useForm<ManufacturerForm>();

  useEffect(() => {
    setOtherError(null);
    reset({ name: existing?.name ?? '', active: existing?.active ?? true });
  }, [value, existing, reset]);

  const save = useSettingsSave<ManufacturerForm, Manufacturer>({
    save: (form) =>
      existing
        ? adminApi.updateManufacturer(existing.id!, form)
        : adminApi.createManufacturer({ name: form.name, active: true }),
    invalidate: CATALOG_KEYS,
    successMessage: isNew ? 'Manufacturer added' : 'Manufacturer saved',
    setError,
    onDone: onClose,
    onOtherError: setOtherError,
  });

  return (
    <DialogFrame
      open={value !== null}
      title={isNew ? 'Add manufacturer' : `Edit ${existing?.name}`}
      onClose={onClose}
      onSave={handleSubmit((form) => save.mutate(form))}
      saving={save.isPending}
      saveLabel={isNew ? 'Add manufacturer' : 'Save'}
      otherError={otherError}
    >
      <TextInput
        label="Name"
        required
        error={formState.errors.name?.message}
        {...register('name', { required: 'A name is required' })}
      />
      {!isNew && <Checkbox label="Active" {...register('active')} />}
    </DialogFrame>
  );
}

interface VendorForm {
  code: string;
  name: string;
  contactEmail: string;
  contactPhone: string;
  active: boolean;
}

export function VendorDialog({ value, onClose }: { value: AdminVendor | 'new' | null; onClose: () => void }) {
  const isNew = value === 'new';
  const existing = value && value !== 'new' ? value : null;
  const [otherError, setOtherError] = useState<unknown>(null);
  const { register, handleSubmit, reset, setError, formState } = useForm<VendorForm>();

  useEffect(() => {
    setOtherError(null);
    reset({
      code: existing?.code ?? '',
      name: existing?.name ?? '',
      contactEmail: existing?.contactEmail ?? '',
      contactPhone: existing?.contactPhone ?? '',
      active: existing?.active ?? true,
    });
  }, [value, existing, reset]);

  const save = useSettingsSave<VendorForm, AdminVendor>({
    save: (form) =>
      existing
        ? adminApi.updateVendor(existing.id, {
            name: form.name,
            contactEmail: form.contactEmail || undefined,
            contactPhone: form.contactPhone || undefined,
            active: form.active,
          })
        : adminApi.createVendor({
            code: form.code,
            name: form.name,
            contactEmail: form.contactEmail || undefined,
            contactPhone: form.contactPhone || undefined,
          }),
    invalidate: CATALOG_KEYS,
    successMessage: isNew ? 'Vendor added' : 'Vendor saved',
    setError,
    onDone: onClose,
    onOtherError: setOtherError,
  });

  return (
    <DialogFrame
      open={value !== null}
      title={isNew ? 'Add vendor' : `Edit ${existing?.name}`}
      onClose={onClose}
      onSave={handleSubmit((form) => save.mutate(form))}
      saving={save.isPending}
      saveLabel={isNew ? 'Add vendor' : 'Save'}
      otherError={otherError}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <TextInput
          label="Code"
          required={isNew}
          disabled={!isNew}
          error={formState.errors.code?.message}
          {...register('code', {
            required: isNew ? 'A code is required' : false,
            pattern: { value: /^[A-Za-z0-9_-]+$/, message: 'Letters, digits, _ and - only' },
          })}
        />
        <TextInput
          label="Name"
          required
          error={formState.errors.name?.message}
          {...register('name', { required: 'A name is required' })}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextInput
          label="Contact email"
          type="email"
          error={formState.errors.contactEmail?.message}
          {...register('contactEmail')}
        />
        <TextInput label="Contact phone" type="tel" {...register('contactPhone')} />
      </div>
      {!isNew && <Checkbox label="Active" {...register('active')} />}
    </DialogFrame>
  );
}
